import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import Logo from '../Logo';

import { useAuth } from '../../state/auth/useAuth';

const Header = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const location = useLocation();
  const { user, role, logout } = useAuth();

  // Role-based navigation items
  const getAllNavigationItems = () => [
    { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard', roles: ['Admin', 'Doctor', 'Receptionist', 'Nurse'] },
    { label: 'Appointments', path: '/appointment-scheduling', icon: 'Calendar', roles: ['Admin', 'Doctor', 'Receptionist'] },
    { label: 'E-Prescribing', path: '/e-prescribing', icon: 'FileText', roles: ['Admin', 'Doctor'] }
  ];

  const navigationItems = getAllNavigationItems().filter(item => 
    item.roles.includes(role)
  );

  const notifications = [
    { id: 1, type: 'urgent', message: 'Lab results ready for Patient #1234', time: '2 min ago' },
    { id: 2, type: 'info', message: 'Appointment reminder: Dr. Smith at 3:00 PM', time: '15 min ago' },
    { id: 3, type: 'warning', message: 'System maintenance scheduled for tonight', time: '1 hour ago' }
  ];

  const handleSearch = (e) => {
    e?.preventDefault();
    console.log('Searching for:', searchQuery);
    setIsSearchOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'k' && (e?.metaKey || e?.ctrlKey)) {
      e?.preventDefault();
      setIsSearchOpen(true);
    }
    if (e?.key === 'Escape') {
      setIsSearchOpen(false);
      setIsNotificationOpen(false);
      setIsUserMenuOpen(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border shadow-elevation-1">
        <div className="flex items-center justify-between h-16 px-4">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center space-x-3">
            <Logo size="sm" showText={true} />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-clinical ${
                  location?.pathname === item?.path
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={item?.icon} size={16} />
                <span>{item?.label}</span>
              </Link>
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-2">
            {/* Search */}
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsSearchOpen(true)}
                className="hidden md:flex"
              >
                <Icon name="Search" size={18} />
              </Button>
              
              {/* Search Modal */}
              {isSearchOpen && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center pt-20">
                  <div className="bg-card rounded-lg shadow-elevation-3 w-full max-w-md mx-4">
                    <form onSubmit={handleSearch} className="p-4">
                      <div className="relative">
                        <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                        <input
                          type="text"
                          placeholder="Search patients, appointments..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e?.target?.value)}
                          className="w-full pl-10 pr-4 py-3 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                          autoFocus
                        />
                      </div>
                      <div className="flex justify-between items-center mt-3 text-xs text-muted-foreground">
                        <span>Press Enter to search</span>
                        <span>ESC to close</span>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>

            {/* Notifications */}
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                className="relative"
              >
                <Icon name="Bell" size={18} />
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-error text-error-foreground text-xs rounded-full flex items-center justify-center">
                  3
                </span>
              </Button>

              {/* Notifications Dropdown */}
              {isNotificationOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-lg shadow-elevation-2 z-50">
                  <div className="p-4 border-b border-border">
                    <h3 className="font-medium text-foreground">Notifications</h3>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {notifications?.map((notification) => (
                      <div key={notification?.id} className="p-4 border-b border-border last:border-b-0 hover:bg-muted transition-clinical">
                        <div className="flex items-start space-x-3">
                          <div className={`w-2 h-2 rounded-full mt-2 ${
                            notification?.type === 'urgent' ? 'bg-error' :
                            notification?.type === 'warning' ? 'bg-warning' : 'bg-primary'
                          }`} />
                          <div className="flex-1">
                            <p className="text-sm text-foreground">{notification?.message}</p>
                            <p className="text-xs text-muted-foreground mt-1">{notification?.time}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-4 border-t border-border">
                    <Button variant="ghost" size="sm" className="w-full">
                      View all notifications
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="relative">
              <Button
                variant="ghost"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center space-x-2 px-3"
              >
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <Icon name="User" size={16} color="white" />
                </div>
                <div className="hidden md:block text-left">
                  <div className="text-sm font-medium text-foreground">
                    {user?.name || user?.email || 'User'}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    
                  </div>
                </div>
                <Icon name="ChevronDown" size={16} />
              </Button>

              {/* User Dropdown */}
              {isUserMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-card border border-border rounded-lg shadow-elevation-2 z-50">
                  <div className="p-4 border-b border-border">
                    <div className="font-medium text-foreground">
                      {user?.name || user?.email || 'User'}
                    </div>
                    <div className="text-sm text-muted-foreground mb-2">
                      {user?.email}
                    </div>
                    
                  </div>
                  <div className="py-2">
                    <button className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-clinical flex items-center space-x-2">
                      <Icon name="User" size={16} />
                      <span>Profile</span>
                    </button>
                    <button className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-clinical flex items-center space-x-2">
                      <Icon name="Settings" size={16} />
                      <span>Settings</span>
                    </button>
                    <Link to="/access-management" className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-clinical flex items-center space-x-2">
                      <Icon name="Shield" size={16} />
                      <span>Access Management</span>
                    </Link>
                  </div>
                  <div className="py-2 border-t border-border">
                    <button 
                      onClick={logout}
                      className="w-full px-4 py-2 text-left text-sm text-error hover:bg-muted transition-clinical flex items-center space-x-2"
                    >
                      <Icon name="LogOut" size={16} />
                      <span>Sign out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
            >
              <Icon name="Menu" size={18} />
            </Button>
          </div>
        </div>
      </header>
      {/* Overlay for dropdowns */}
      {(isNotificationOpen || isUserMenuOpen) && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            setIsNotificationOpen(false);
            setIsUserMenuOpen(false);
          }}
        />
      )}
    </>
  );
};

export default Header;